import React, { useState, useEffect, useRef } from 'react';
import { adminService } from '../../../services/adminService';
import { clearLegalCache } from '../../../services/legalService';
import { TERMS_MARKDOWN } from '../../../content/legal/termsContent';
import { PRIVACY_MARKDOWN } from '../../../content/legal/privacyContent';
import './AdminPrivacyTermsPage.css';

interface AdminPrivacyTermsPageProps {
  embedded?: boolean;
}

const AdminPrivacyTermsPage: React.FC<AdminPrivacyTermsPageProps> = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [privacyMarkdown, setPrivacyMarkdown] = useState('');
  const [termsMarkdown, setTermsMarkdown] = useState('');

  const lastSavedSnapshot = useRef<string | null>(null);

  const formSnapshot = () =>
    JSON.stringify({ privacy: privacyMarkdown, terms: termsMarkdown });

  const hasChanges =
    lastSavedSnapshot.current !== null && formSnapshot() !== lastSavedSnapshot.current;

  const canSave = hasChanges && !saving;

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminService.getLegalSettings();
      // When DB is empty, pre-fill with built-in default content so admin can edit/save it
      const privacy =
        data.privacy_markdown != null && data.privacy_markdown.trim() !== ''
          ? data.privacy_markdown
          : PRIVACY_MARKDOWN;
      const terms =
        data.terms_markdown != null && data.terms_markdown.trim() !== ''
          ? data.terms_markdown
          : TERMS_MARKDOWN;
      setPrivacyMarkdown(privacy);
      setTermsMarkdown(terms);
      lastSavedSnapshot.current = JSON.stringify({ privacy, terms });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : 'Failed to load Privacy / Terms content';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await adminService.updateLegalSettings({
        privacy_markdown: privacyMarkdown,
        terms_markdown: termsMarkdown,
      });

      clearLegalCache();
      setSuccessMessage('Privacy / Terms & Conditions saved successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      lastSavedSnapshot.current = formSnapshot();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : 'Failed to save Privacy / Terms content';
      setError(String(message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className={
          embedded
            ? 'admin-privacy-terms-loading admin-privacy-terms-loading-embedded'
            : 'admin-privacy-terms-loading'
        }
      >
        <div className="admin-privacy-terms-loading-spinner" />
      </div>
    );
  }

  const content = (
    <div className="admin-privacy-terms-container">

      {error && (
        <div className="admin-privacy-terms-alert admin-privacy-terms-alert-error">
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {successMessage && (
        <div className="admin-privacy-terms-alert admin-privacy-terms-alert-success">
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          {successMessage}
        </div>
      )}

      {hasChanges && (
        <div className="admin-privacy-terms-alert admin-privacy-terms-alert-unsaved">
          <svg fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          You have unsaved changes. Save before leaving or you may lose them.
        </div>
      )}

      <div className="admin-privacy-terms-content">
        <div className="admin-privacy-terms-section">
          <h2 className="admin-privacy-terms-section-title">Terms & Conditions</h2>
          <p className="admin-privacy-terms-help">Markdown shown as Terms of Service. Leave empty and save to show nothing to users.</p>
          <textarea
            className="admin-privacy-terms-textarea custom-scrollbar"
            value={termsMarkdown}
            onChange={(e) => setTermsMarkdown(e.target.value)}
            placeholder="# Terms and Conditions&#10;&#10;Enter markdown content..."
            rows={16}
            spellCheck={false}
          />
        </div>
        <div className="admin-privacy-terms-section">
          <h2 className="admin-privacy-terms-section-title">Privacy Policy</h2>
          <p className="admin-privacy-terms-help">Markdown shown as Privacy Policy. Leave empty and save to show nothing to users.</p>
          <textarea
            className="admin-privacy-terms-textarea custom-scrollbar"
            value={privacyMarkdown}
            onChange={(e) => setPrivacyMarkdown(e.target.value)}
            placeholder="# Privacy Policy&#10;&#10;Enter markdown content..."
            rows={16}
            spellCheck={false}
          />
        </div>
      </div>

      <div className="admin-privacy-terms-footer">
        <button
          className="admin-privacy-terms-btn-save"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? (
            <>
              <div className="admin-privacy-terms-btn-spinner" />
              Saving...
            </>
          ) : (
            <>
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (embedded) return content;
  return (
    <div className="admin-privacy-terms-page">
      <div className="admin-privacy-terms-bg">
        <div className="admin-privacy-terms-bg-1" />
        <div className="admin-privacy-terms-bg-2" />
      </div>
      {content}
    </div>
  );
};

export default AdminPrivacyTermsPage;
